import { getProducts, createEmptyCart, addProductToCart } from './_graphql';
import { getMindboxPrices, type LandingPriceInfo } from './_mindbox';

const $productNotification = document.querySelector('[data-role=notification]') as HTMLDivElement;
const $productName = $productNotification.querySelector('[data-role=product-name]');
const $productImage = $productNotification.querySelector('[data-role=product-image]') as HTMLImageElement;
const $productPricePopup = $productNotification.querySelector('[data-role=regular-price]');
const $productBasePricePopup = $productNotification.querySelector('[data-role=old-price]');
const $cartQty = document.querySelector('[data-role=cart-counter]');
const cart: Record<string, number> = {};
const $closeButton = document.querySelectorAll('[data-role=close-button]');

declare let productsSku: string[];

const skusArr = [...new Set(productsSku.filter(Boolean))];
let pricesCache: Record<string, LandingPriceInfo> = {};

const formatRub = (n: number) =>
	`${n.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽`;

const getProductPrices = async () => {
	const products = await getProducts(skusArr);
	const prices = await getMindboxPrices(products);

	pricesCache = prices;

	products.forEach(product => {
		const priceInfo = prices[product.sku];
		const $productPrices = document.querySelectorAll(`[data-role=product-price-${product.sku}]`);
		const $productBasePrices = document.querySelectorAll(`[data-role=product-base-price-${product.sku}]`);
		const $productButtons = document.querySelectorAll(`[data-role=product-button-${product.sku}]`);

		if (priceInfo) {
			const finalPrice = priceInfo.finalPrice ?? priceInfo.basePrice;
			const regularPrice = priceInfo.basePrice;

			$productPrices.forEach($productPrice => {
				if (typeof $productPrice.textContent === 'string') {
					$productPrice.textContent = formatRub(finalPrice);
				}
			});

			$productBasePrices.forEach($productBasePrice => {
				if (regularPrice > finalPrice && typeof $productBasePrice.textContent === 'string') {
					$productBasePrice.textContent = formatRub(regularPrice);
					$productBasePrice.classList.add('available');
				}
			});
		}

		if (product.stock_status === 'IN_STOCK') {
			$productPrices.forEach($productPrice => {
				$productPrice.classList.add('available');
			});

			$productButtons.forEach($productButton => {
				$productButton.classList.add('available');
			});
		}
	});
};

document.addEventListener('click', (event) => {
	const $target = event.target as HTMLElement;
	const $variant = $target.closest('[data-variant]') as HTMLElement;

	if ($variant) {
		const $product = $variant.closest('[data-role=product-item]');

		$product?.classList.toggle('_alt', $variant.dataset.variant === '_alt');
		$product?.classList.toggle('_orig', $variant.dataset.variant === '_orig');
	}
});

document.addEventListener('submit', async (event) => {
	event.preventDefault();

	const $form = event.target as HTMLFormElement;

	if ($form.dataset.role !== 'add-to-cart-form') {
		return;
	}

	const formData = new FormData($form);
	const sku = formData.get('sku') as string | null;

	if (!sku) {
		return;
	}

	let quantity = cart[sku] ? cart[sku] + 1 : 1;

	let cartId = document.cookie.match(/(?:^|;\s*)cart_id=([^;]*)/)?.[1];

	if (!cartId) {
		cartId = await createEmptyCart();
		if (!cartId) return;
		document.cookie = `cart_id=${cartId}; path=/; domain=${window.location.hostname}; SameSite=Lax; Secure`; 
	}

	const result = await addProductToCart(cartId, sku, quantity);

	if (!result) {
		return;
	}

	const products = await getProducts([sku]);
	const product = products[0];

	if (!product) {
		return;
	}

	cart[sku] = quantity;
	const basketCount = Object.values(cart).reduce((sum, current) => sum + current, 0);
	const priceInfo = pricesCache[sku];
	const finalPrice = priceInfo?.finalPrice ?? priceInfo?.basePrice;
	const regularPrice = priceInfo?.basePrice;

	if (typeof $productName?.textContent === 'string') {
		$productName.textContent = product.name;
	}

	if ($productImage) {
		const $slide = $form.closest('.swiper-slide');
		const isCareProgram = $slide?.closest('[data-role="care-program"]');
		
		let imgSelector;
		if (isCareProgram) {
			const isProtection = $form.classList.contains('protection');
			imgSelector = isProtection ? '.protection img' : '.default img';
		} else {
			const isAlt = $form.classList.contains('_alt');
			imgSelector = isAlt ? '._alt img' : '._orig img';
		}
		
		const $productImg = $slide?.querySelector(imgSelector);
		if ($productImg) {
			$productImage.src = $productImg.getAttribute('src') || '';
		}
	}

	if (typeof $productPricePopup?.textContent === 'string' && finalPrice) {
		$productPricePopup.textContent = formatRub(finalPrice);
	}

	if (typeof $productBasePricePopup?.textContent === 'string' && regularPrice && finalPrice && regularPrice > finalPrice) {
		$productBasePricePopup.textContent = formatRub(regularPrice);
	}

	if (basketCount && typeof $cartQty?.textContent === 'string') {
		$cartQty.textContent = String(basketCount);
	}

	$productNotification.classList.add('_show');
});

if ($closeButton) {
	$closeButton.forEach(($el) => {
		$el.addEventListener('click', () => $productNotification.classList.remove('_show'));
	});
}

getProductPrices();
